import { useEffect, useState } from "react";

/** Format whole seconds as mm:ss (e.g. 90 → "01:30", 30 → "00:30"). */
export function formatMmSs(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

/**
 * Live seconds remaining until an ISO `targetIso`, ticking every second, never
 * below zero. Returns null when no target is given. Used by the rotation-block
 * countdown (assigned surface) and any until-time display.
 */
export function useSecondsUntil(targetIso: string | null | undefined): number | null {
  const compute = () => {
    if (!targetIso) return null;
    const ms = new Date(targetIso).getTime() - Date.now();
    // Ceil so a freshly-loaded "30s remaining" reads 00:30 (not 00:29 from a
    // sub-second of elapsed load time) and the display only drops a second once
    // a full second has actually passed.
    return Math.max(0, Math.ceil(ms / 1000));
  };
  const [secs, setSecs] = useState<number | null>(compute);

  useEffect(() => {
    if (!targetIso) {
      setSecs(null);
      return;
    }
    setSecs(compute());
    const id = setInterval(() => setSecs(compute()), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetIso]);

  return secs;
}

/**
 * A fixed-length countdown from `seconds` down to 0, ticking each second, that
 * resets whenever `resetKey` changes (e.g. a fresh pass token). Used for the
 * QR pass "refreshes in 00:NN" cadence display.
 */
export function useCountdownFrom(seconds: number, resetKey: unknown): number {
  const [remaining, setRemaining] = useState(seconds);

  useEffect(() => {
    setRemaining(seconds);
    const id = setInterval(() => {
      setRemaining((r) => (r > 0 ? r - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey, seconds]);

  return remaining;
}
